import {
  type DayOfEmergencyDto,
  type DayOfEmergencyParticipantDto,
  type DayOfEmergencyTimelineEntryDto,
  type EmergencyAccessValidationResponseDto,
  type EmergencyDeclarationDto,
  type ValidateLawEnforcementEmergencyAccessRequestDto,
} from "@crowdcoursing/api/openapi";

/**
 * Anonymous law-enforcement command-center HTTP API.
 *
 * **Why raw `fetch` for most routes?** The app-wide OpenAPI client (`configureApi` in `App.tsx`)
 * merges `Authorization: Bearer …` after request headers; anonymous magic-link calls should not
 * rely on JWT. Bubble context and admin contacts use generated `fetchDayOfEmergencyAnonymousAccess*`
 * from `@crowdcoursing/api/openapi` with `baseUrlOverride` + `X-Emergency-Access-Token` in the page.
 * Remaining day/participants/timeline/declarations helpers stay as raw `fetch` until those are
 * switched the same way (or a dedicated anonymous fetcher is added in `packages/api`).
 *
 * Path templates stay aligned with `DayOfEmergencyAnonymousAccessController` and the OpenAPI spec.
 *
 * **Backend contract (LE notifications):** If the server sends law enforcement any email or SMS for
 * an emergency (live: LE included in bubble settings for that type; drill: LE included via drill flag),
 * that message must always include the minted law enforcement command-center link (`pathHint` + token).
 * There is no case where LE is notified but should not receive the link—the decision to notify is the
 * same as the decision to include access. Implemented in the API/email/SMS pipeline, not this app.
 */

/**
 * Must match API `DayOfEmergencyAnonymousAccessController` and minted link instructions.
 * Paths are always keyed by `dayOfEmergencyId` (the reunification day), never by individual declaration id.
 */
export const LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER = "X-Emergency-Access-Token";

/** Relative to API base URL. */
export function dayOfEmergencyAnonymousApiBasePath(dayOfEmergencyId: string): string {
  return `/api/v1/day-of-emergency/${encodeURIComponent(dayOfEmergencyId)}`;
}

export const lawEnforcementAnonymousPaths = {
  getActiveDeclarations: (dayOfEmergencyId: string) =>
    `${dayOfEmergencyAnonymousApiBasePath(dayOfEmergencyId)}/law-enforcement/emergency-declarations/active`,
  getDay: (dayOfEmergencyId: string) => `${dayOfEmergencyAnonymousApiBasePath(dayOfEmergencyId)}/law-enforcement/day`,
  getParticipants: (dayOfEmergencyId: string) =>
    `${dayOfEmergencyAnonymousApiBasePath(dayOfEmergencyId)}/law-enforcement/participants`,
  getTimeline: (dayOfEmergencyId: string) =>
    `${dayOfEmergencyAnonymousApiBasePath(dayOfEmergencyId)}/law-enforcement/timeline`,
  validateLawEnforcement: (dayOfEmergencyId: string) =>
    `${dayOfEmergencyAnonymousApiBasePath(dayOfEmergencyId)}/access/validate-law-enforcement`,
} as const;

function joinUrl(baseUrl: string, path: string): string {
  const b = baseUrl.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
}

async function getJson<T>(url: string, token: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    headers: {
      Accept: "application/json",
      [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token,
    },
    method: "GET",
    signal,
  });
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`) as Error & { status: number };
    err.status = response.status;
    throw err;
  }
  return response.json() as Promise<T>;
}

async function postJson<TResponse>(url: string, body: unknown, signal?: AbortSignal): Promise<TResponse> {
  const response = await fetch(url, {
    body: JSON.stringify(body),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "POST",
    signal,
  });
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`) as Error & { status: number };
    err.status = response.status;
    throw err;
  }
  return response.json() as Promise<TResponse>;
}

/**
 * Audited validation (IP / user-agent on server). Call once when opening the command center;
 * read-only GETs still require the access token header.
 */
export function validateLawEnforcementEmergencyAccess(
  baseUrl: string,
  dayOfEmergencyId: string,
  token: string,
  signal?: AbortSignal,
): Promise<EmergencyAccessValidationResponseDto> {
  const url = joinUrl(baseUrl, lawEnforcementAnonymousPaths.validateLawEnforcement(dayOfEmergencyId));
  const body: ValidateLawEnforcementEmergencyAccessRequestDto = { token };
  return postJson<EmergencyAccessValidationResponseDto>(url, body, signal);
}

export function fetchLawEnforcementEmergencyDay(
  baseUrl: string,
  dayOfEmergencyId: string,
  token: string,
  signal?: AbortSignal,
): Promise<DayOfEmergencyDto> {
  const url = joinUrl(baseUrl, lawEnforcementAnonymousPaths.getDay(dayOfEmergencyId));
  return getJson<DayOfEmergencyDto>(url, token, signal);
}

export function fetchLawEnforcementEmergencyParticipants(
  baseUrl: string,
  dayOfEmergencyId: string,
  token: string,
  signal?: AbortSignal,
): Promise<DayOfEmergencyParticipantDto[]> {
  const url = joinUrl(baseUrl, lawEnforcementAnonymousPaths.getParticipants(dayOfEmergencyId));
  return getJson<DayOfEmergencyParticipantDto[]>(url, token, signal);
}

export function fetchLawEnforcementEmergencyTimeline(
  baseUrl: string,
  dayOfEmergencyId: string,
  token: string,
  signal?: AbortSignal,
): Promise<DayOfEmergencyTimelineEntryDto[]> {
  const url = joinUrl(baseUrl, lawEnforcementAnonymousPaths.getTimeline(dayOfEmergencyId));
  return getJson<DayOfEmergencyTimelineEntryDto[]>(url, token, signal);
}

export function fetchLawEnforcementEmergencyActiveDeclarations(
  baseUrl: string,
  dayOfEmergencyId: string,
  token: string,
  signal?: AbortSignal,
): Promise<EmergencyDeclarationDto[]> {
  const url = joinUrl(baseUrl, lawEnforcementAnonymousPaths.getActiveDeclarations(dayOfEmergencyId));
  return getJson<EmergencyDeclarationDto[]>(url, token, signal);
}
